import React, { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../../../../../../app/hooks'
import {
  AttachmentData,
  AttachmentDataWithRequiredAttachment,
  AttachmentStatus,
  AttachmentType,
} from '../../../../../../../../../../@types/types'
import { useFeatureSwitch } from '../../../../../../../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../../../../../../../utils/features/featureSwitches'
import { isWebView } from '../../../../../../../../../../utils/navigation'
import { WebViewMultimediaPreview } from './WebViewMultimediaPreview'
import { InitialUploadClickableArea } from './InitialUploadClickableArea'
import styles from './MobileMultimediaPreview.module.css'

interface MobileMultimediaButtonProps {
  dropzoneId: string
}

export const WebViewMultimediaButton = forwardRef<HTMLDivElement, MobileMultimediaButtonProps>(
  ({ dropzoneId }, ref) => {
    const { t } = useTranslation()
    const attachments = useAppSelector((state) => state.multimediaFeature.attachments) ?? []
    const isMobileFlow = useAppSelector((state) => state.flowFeature.flowInformation.isMobileFlow)
    const { isActive: isInlinePicturesEnabled } = useFeatureSwitch(
      FeatureSwitch.PPA_13285_RENT_SALE_MOBILE_NEW_IMAGE_UPLOAD,
      isWebView() && isMobileFlow
    )
    const [deletedPictureIds, setDeletedPictureIds] = useState<string[]>([])
    const pictureAttachments = attachments.filter(
      (item): item is AttachmentData =>
        item.attachmentType === AttachmentType.PICTURE &&
        ((item.state === AttachmentStatus.PERSISTED && !!item.attachment?.url) ||
          (item.state === AttachmentStatus.UPLOADING && !!item.file)) &&
        !deletedPictureIds.includes(item.attachment?.id ?? '')
    )
    const pictures = pictureAttachments.filter(
      (item): item is AttachmentDataWithRequiredAttachment =>
        item.state === AttachmentStatus.PERSISTED && !!item.attachment?.url
    )
    const hasPictureAttachments = isInlinePicturesEnabled ? pictureAttachments.length > 0 : pictures.length > 0

    return (
      <div id={dropzoneId}>
        <div ref={ref} style={{ display: 'none' }} />
        {hasPictureAttachments ? (
          <WebViewMultimediaPreview
            dropzoneId={`${dropzoneId}-add`}
            pictureAttachments={pictureAttachments}
            pictures={pictures}
            isInlinePicturesEnabled={isInlinePicturesEnabled}
            onPictureDeleted={(pictureId) => setDeletedPictureIds((ids) => [...ids, pictureId])}
          />
        ) : (
          <InitialUploadClickableArea dropzoneId={`${dropzoneId}-empty`} />
        )}
        {isInlinePicturesEnabled && (
          <p className={`${styles.tip} font-body-small-regular font-center margin-horizontal-m`}>
            {t('wizard.screen.multimedia.webview.inlinePictures.uploadTip')}
          </p>
        )}
      </div>
    )
  }
)
